import { gsap } from 'gsap';
import { Container, Sprite } from '../../odie';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { MovieClip } from '@play-co/timestep-core/ui';
import { getMatch2AssetPath } from '../util/assets';

export type FleeingButterflyAnimationOptions = {
  scale: number;
  rotation: number;
};

const manifest = () => ({
  movieclip: {
    url: getMatch2AssetPath('blocks/butterfly'),
    loadMethod: MovieClip.animationLoader,
  },
  explosion: 'fx.block/fx.block.explode.png',
});

export class FleeingButterflyAnimation extends Container implements IAnimation {
  private _options: FleeingButterflyAnimationOptions;

  constructor(options: FleeingButterflyAnimationOptions) {
    super();
    this._options = options;
  }

  public static assets() {
    return Object.values(manifest());
  }

  public async start(): Promise<void> {
    // create butterfly
    const butterfly = this._createButterfly();

    // create blast
    const blast = this._createBlast();

    // animate butterfly and blast
    await Promise.all([
      this._animateButterfly(butterfly),
      this._animateBlast(blast),
    ]);
  }

  public stop() {}

  private async _animateButterfly(butterfly: Sprite) {
    // spawn butterfly
    this.addChild(butterfly);

    const duration = 0.75;
    const scale = 1.2;
    const rotation = this._options.rotation - Math.PI * 0.5 - 0.3;
    const distance = 200;
    const x = Math.cos(rotation) * distance;
    const y = Math.sin(rotation) * distance;

    // animate
    await gsap
      .timeline()
      .to(butterfly, {
        duration,
        alpha: 0,
        ease: 'quad.in',
      })
      .to(
        butterfly,
        {
          duration,
          scaleX: scale,
          scaleY: scale,
          ease: 'quad.inOut',
        },
        0,
      )
      .to(
        butterfly,
        {
          duration,
          y,
          ease: 'none',
        },
        0,
      )
      .to(
        butterfly,
        {
          duration,
          x,
          ease: 'quad.in',
        },
        0,
      );

    // despawn butterfly
    this.removeChild(butterfly);
  }

  private async _animateBlast(blast: Sprite) {
    // set initial values
    blast.scaleX = 0.2;
    blast.scaleY = 0.2;

    // spawn blast
    this.addChild(blast);

    // animate
    await gsap.to(blast, {
      scaleX: 1.6,
      scaleY: 1.6,
      duration: 0.2,
      ease: 'power1.out',
    });

    // despawn blast
    this.removeChild(blast);
  }

  private _createButterfly(): Sprite {
    const butterfly = new Sprite();
    butterfly.anchorX = 0.5;
    butterfly.anchorY = 0.5;
    butterfly.rotation = this._options.rotation;
    butterfly.scaleX = this._options.scale;
    butterfly.scaleY = this._options.scale;

    const movieclip = new MovieClip({
      superview: butterfly.view,
      url: manifest().movieclip.url,
      scale: 0.9,
    });
    movieclip.play('worm_state03', null, true);
    return butterfly;
  }

  private _createBlast(): Sprite {
    const blast = Sprite.from(manifest().explosion);
    blast.anchorX = 0.5;
    blast.anchorY = 0.5;
    return blast;
  }
}
